import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileSpreadsheet,
  Presentation,
  FileText,
  Download,
  DollarSign,
  ClipboardCheck,
  Code,
  HardHat,
} from "lucide-react";
import { useEffect, useState } from "react";
import { getTemplates, Template, recordDownload } from "@/data/templates";
import { useAuth } from "@/auth/SupabaseAuthProvider";
import { Link } from "react-router-dom";

const fileTypeConfig: Record<string, { icon: any; color: string; bg: string }> =
  {
    Excel: {
      icon: FileSpreadsheet,
      color: "text-emerald-600",
      bg: "bg-emerald-500/10",
    },
    PowerPoint: {
      icon: Presentation,
      color: "text-orange-600",
      bg: "bg-orange-500/10",
    },
    "Word/PDF": {
      icon: FileText,
      color: "text-blue-600",
      bg: "bg-blue-500/10",
    },
  };

const generateFilename = (template: Template): string => {
  const cleanTitle = template.title
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "_")
    .toLowerCase();

  let extension = "";
  switch (template.fileType) {
    case "Excel":
      extension = ".xlsx";
      break;
    case "PowerPoint":
      extension = ".pptx";
      break;
    case "Word/PDF":
      if (template.file_url.toLowerCase().includes(".pdf")) {
        extension = ".pdf";
      } else if (template.file_url.toLowerCase().includes(".doc")) {
        extension = ".docx";
      } else {
        extension = ".docx";
      }
      break;
    default:
      extension = ".pdf";
  }

  return `${cleanTitle}${extension}`;
};

const downloadFileWithName = async (
  url: string,
  filename: string,
): Promise<void> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Error downloading file:", error);
    window.open(url, "_blank");
  }
};

const TemplateCard = ({ template }: { template: Template }) => {
  const { user } = useAuth();
  const fileConfig = fileTypeConfig[template.fileType] || {
    icon: FileText,
    color: "text-gray-600",
    bg: "bg-gray-500/10",
  };
  const FileIcon = fileConfig.icon;
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await recordDownload(template.id, user?.id, user?.email);
      const filename = generateFilename(template);
      await downloadFileWithName(template.file_url, filename);
    } catch (error) {
      console.error("Error downloading template:", error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card className="flex flex-col h-full hover:shadow-lg hover:border-primary/20 transition-all duration-200">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className={`p-3 rounded-xl ${fileConfig.bg}`}>
            <FileIcon className={`h-6 w-6 ${fileConfig.color}`} />
          </div>
          <Badge variant="outline" className="text-xs">
            {template.fileType}
          </Badge>
        </div>
        <CardTitle className="text-lg mt-4">{template.title}</CardTitle>
        <CardDescription className="text-sm leading-relaxed">
          {template.description}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-grow" />

      <CardFooter className="pt-0">
        <Button
          variant="outline"
          className="w-full"
          onClick={handleDownload}
          disabled={downloading}
        >
          {downloading ? (
            <>
              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              Downloading...
            </>
          ) : (
            <>
              <Download className="mr-2 h-4 w-4" />
              Download
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

const Templates = () => {
  const [financialTemplates, setFinancialTemplates] = useState<Template[]>([]);
  const [operationsTemplates, setOperationsTemplates] = useState<Template[]>(
    [],
  );
  const [developmentTemplates, setDevelopmentTemplates] = useState<Template[]>(
    [],
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const allTemplates = await getTemplates();

      const financial = allTemplates.filter((t) => t.category === "financial");
      const operations = allTemplates.filter(
        (t) => t.category === "operations",
      );
      const development = allTemplates.filter(
        (t) => t.category === "development",
      );

      setFinancialTemplates(financial);
      setOperationsTemplates(operations);
      setDevelopmentTemplates(development);
    } catch (err) {
      console.error("Error fetching templates:", err);
      setFinancialTemplates([]);
      setOperationsTemplates([]);
      setDevelopmentTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <main className="container py-12">
          <div className="mb-10">
            <h1 className="text-3xl font-bold tracking-tight mb-2">
              Templates & Resources
            </h1>
            <p className="text-muted-foreground text-lg">
              Download ready-to-use documents to accelerate your hotel project
            </p>
          </div>

          <section className="mb-12">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              Financial Templates
            </h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="p-3 rounded-xl bg-gray-200"></div>
                      <div className="h-6 w-16 bg-gray-200 rounded"></div>
                    </div>
                    <div className="h-6 bg-gray-200 rounded mt-4"></div>
                    <div className="h-4 bg-gray-200 rounded mt-2"></div>
                    <div className="h-4 bg-gray-200 rounded mt-1"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="h-10 bg-gray-200 rounded w-full"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-primary" />
              Operations Templates
            </h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="p-3 rounded-xl bg-gray-200"></div>
                      <div className="h-6 w-16 bg-gray-200 rounded"></div>
                    </div>
                    <div className="h-6 bg-gray-200 rounded mt-4"></div>
                    <div className="h-4 bg-gray-200 rounded mt-2"></div>
                    <div className="h-4 bg-gray-200 rounded mt-1"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="h-10 bg-gray-200 rounded w-full"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <HardHat className="h-5 w-5 text-primary" />
              Development Templates
            </h2>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="p-3 rounded-xl bg-gray-200"></div>
                      <div className="h-6 w-16 bg-gray-200 rounded"></div>
                    </div>
                    <div className="h-6 bg-gray-200 rounded mt-4"></div>
                    <div className="h-4 bg-gray-200 rounded mt-2"></div>
                    <div className="h-4 bg-gray-200 rounded mt-1"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="h-10 bg-gray-200 rounded w-full"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <main className="container py-12">
        <div className="mb-10">
          <h1 className="text-3xl font-bold tracking-tight mb-2">
            Templates & Resources
          </h1>
          <p className="text-muted-foreground text-lg">
            Download ready-to-use documents to accelerate your hotel project
          </p>
        </div>

        <section className="mb-12">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-primary" />
            Financial Templates
          </h2>
          {financialTemplates.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {financialTemplates.map((template) => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 border rounded-lg border-dashed">
              <p className="text-muted-foreground">
                No financial templates available
              </p>
            </div>
          )}
        </section>

        <section className="mb-12">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-primary" />
            Operations Templates
          </h2>
          {operationsTemplates.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {operationsTemplates.map((template) => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 border rounded-lg border-dashed">
              <p className="text-muted-foreground">
                No operations templates available
              </p>
            </div>
          )}
        </section>

        <section className="mb-12">
          <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <HardHat className="h-5 w-5 text-primary" />
            Development Templates
          </h2>
          {developmentTemplates.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {developmentTemplates.map((template) => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 border rounded-lg border-dashed">
              <p className="text-muted-foreground">
                No development templates available
              </p>
            </div>
          )}
        </section>

        <div className="mt-16 text-center p-8 rounded-2xl bg-muted/50 border border-border">
          <h3 className="text-xl font-semibold mb-2">
            Need a custom template?
          </h3>
          <p className="text-muted-foreground mb-4">
            Our team can create tailored documents for your specific hotel
            concept.
          </p>
          <Button className="bg-primary-blue" asChild>
            <Link to="/consultancy">Request Custom Template</Link>
          </Button>
        </div>
      </main>
    </div>
  );
};

export default Templates;
